import React, { useEffect, useState } from 'react';
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { parseCountryCallingCode } from '@utils/DataMappingUtils';
import { DateHelper } from '@utils/DateHelper';
import { FormHelper } from '@utils/FormHelper';
import { WhatsappIcon } from '@assets/index';
import { fetchProfileUserDetailsSelector } from '@redux/profile/selectors';
import { ProfileModel } from '@models/Profile';
import { MAX_AGE_YEARS, MIN_AGE_YEARS } from "@constants/config";
import { UserLocalDetailsByIpService } from '@webServices/UserLocalDetailsByIpService';
import { authMessages } from '@containers/Auth/message';
import { dashboardMessages } from '@containers/Dashboard/messages';
import { translate } from "@translations/Translater";
import AnimatedSearchDropdown from '@components/atoms/Dropdown';
import CountryCodePhoneNumber, { CountryPhoneInputValue } from '@components/CountryCodePhoneNumber';
import Text, { FontFamilyVariant, TextType } from '@components/Text';
import { CheckBox } from '@components/WFThinkDesign/Checkbox';
import { Img } from "@components/Image";
import { IBasicInfo } from '@presentation/profileCompletion';
import './styles.less';

interface IProps {
    userProfile: ProfileModel;
    basicInfo: IBasicInfo,
    setBasicInfo: React.Dispatch<React.SetStateAction<IBasicInfo>>;
}

export interface IPhoneNumber {
    countryCode: number;
    number: string;
}

function ProfileFields(props: IProps) {
    const { t } = useTranslation();

    const { userProfile, basicInfo, setBasicInfo } = props;

    const [gender, setGender] = useState('');
    const [birthYear, setBirthYear] = useState<number>(undefined);
    const [phoneNumberError, setPhoneNumberError] = useState('');
    const [enableWhatsappNotification, setEnableWhatsappNotification] = useState(false);
    const [phone, setPhone] = useState<IPhoneNumber>({
        number: "",
        countryCode: 0,
    });


    const genderList = [
        {
            key: "MALE",
            value: `${translate(t, authMessages.male)}`,
        },
        {
            key: "FEMALE",
            value: `${translate(t, authMessages.female)}`,
        },
        {
            key: "OTHERS",
            value: `${translate(t, authMessages.others)}`,
        },
    ];

    useEffect(() => {
        if (Object.keys(userProfile)?.length > 0) {
            setFieldsValues();
        }
    }, [userProfile]);

    useEffect(() => {
        if (!phoneNumberError && phone?.number?.length > 0) {
            setEnableWhatsappNotification(true)
        } else (
            setEnableWhatsappNotification(false)
        )
    }, [phoneNumberError, phone?.number])

    useEffect(() => {
        const updatedData = {
            ...basicInfo,
            enableWhatsappNotification: enableWhatsappNotification
        }
        setBasicInfo(updatedData)
    }, [enableWhatsappNotification])

    const setFieldsValues = () => {
        setGender(t(userProfile?.getGender() ? `global.labelsAndTitles.${userProfile?.getGender()}` : ""));
        setBirthYear(userProfile?.getYearOfBirth());
        userProfile?.getCountryCode() ?
            setPhone({
                countryCode: Number(userProfile?.getCountryCode()?.slice(0)),
                number: userProfile?.getPhoneNumber() ? userProfile?.getPhoneNumber() : ""
            })
            :
            initializePhoneNumber();
    }

    const currentYear = DateHelper.getYear();

    const birthYearsList = [...new Array(MAX_AGE_YEARS - MIN_AGE_YEARS)].map(
        (value, index) => {
            const year = currentYear - MIN_AGE_YEARS - index;
            return {
                key: year + "",
                value: year + "",
            };
        }
    );

    const initializePhoneNumber = async () => {
        UserLocalDetailsByIpService.getInstance().then((localeDetails) => {
            const countryCode = parseCountryCallingCode(
                localeDetails.getCountryCallingCode()
            );
            setPhone({
                countryCode,
                number: userProfile.getPhoneNumber(),
            });
        });
    };

    const onGenderChange = (value) => {
        setGender(value);
        const updatedData = {
            ...basicInfo,
            gender: genderList.find((g) => g.value === value)?.key || undefined
        }
        setBasicInfo(updatedData)
    }

    const handleChangeYearofBirth = (value) => {
        setBirthYear(value);
        const updatedData = {
            ...basicInfo,
            yearOfBirth: value as string
        }
        setBasicInfo(updatedData)
    };

    const setPhoneNumber = (value: CountryPhoneInputValue) => {
        if (value && value.phone && value.phone.length > 0) {
            setPhoneNumberError(FormHelper.PhoneNumberValidation(value));
        } else {
            setPhoneNumberError("");
        }
        setPhone({
            countryCode: value?.code,
            number: value?.phone,
        });
        const updatedData = {
            ...basicInfo,
            phoneNumber: value,
            phone: {
                countryCode: value?.code,
                number: value?.phone,
            }
        }
        setBasicInfo(updatedData)
    };

    const onChangeWhatsappNotification = () => {
        if (!phoneNumberError && phone?.number?.length > 0) {
            setEnableWhatsappNotification(!enableWhatsappNotification);
        }
    }

    return (
        <div className="basicInfo_fields_wrapper">
            <Text
                textType={TextType.h8}
                fontFamilyVariant={FontFamilyVariant.regular}
                className={"subHeading"}
                text={translate(t, dashboardMessages.basicInformation)}
            />
            <AnimatedSearchDropdown
                data={genderList.filter((option) => gender.toLowerCase() !== option.value.toLowerCase())}
                disabled={false}
                isEmptyList={false}
                label={translate(t, authMessages.gender)}
                onChange={onGenderChange}
                prefill={gender}
                searchInputClassName="gender_selection"
                className="gender_selection"
            />

            <AnimatedSearchDropdown
                data={birthYearsList.filter((option) => birthYear?.toString() !== option.value)}
                disabled={false}
                isEmptyList={false}
                label={translate(t, authMessages.yearOfBirth)}
                onChange={handleChangeYearofBirth}
                prefill={birthYear}
                searchInputClassName="birth_year_selection"
                className="birth_year_selection"
            />

            <div id={'mobile-number'}>
                <CountryCodePhoneNumber
                    id="mobile"
                    value={{
                        code: phone.countryCode,
                        phone: phone.number,
                    }}
                    placeholder={translate(t, dashboardMessages.mobileNumberMandatory)}
                    disabled={false}
                    onChange={setPhoneNumber}
                    popUpContainerId={"mobile-number"}
                    isPrivate={false}
                    error={phoneNumberError}
                />
            </div>
            <div className="whatsapp_wrapper">
                <CheckBox
                    label={translate(t, authMessages.whatsAppNotification)}
                    onPress={onChangeWhatsappNotification}
                    className="whatsapp_notification_checkbox" isChecked={enableWhatsappNotification}
                />
                <Img alt="Whatsapp" className="whatsapp_Icon" src={WhatsappIcon} />
                <Text
                    className={'whatsapp_text'}
                    fontFamilyVariant={FontFamilyVariant.medium}
                    text={translate(t, authMessages.whatsApp)}
                    textType={TextType.paragraph1}
                />
            </div>
        </div>
    );
}

const mapStateToProps = (state: any) => {
    return {
        userProfile: fetchProfileUserDetailsSelector(state),
    };
};

const mapDispatchToProps = (dispatch: any) => {
    return {
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(ProfileFields);